import { getTerminalConfig } from "../../config/terminalConfig";
import { networkTrace, shouldTraceNetworkDebug } from "./networkTrace";

// TEMP diagnostic. Installs a pass-through window.fetch wrapper + online/offline/Network-Information
// listeners that feed networkTrace, so all HTTP and connectivity events show on the kiosk screen via
// NetworkDebugOverlay. Installed once at startup when the flag is on; each handler ALSO re-checks the
// flag per event, so `?networkDebug=false` halts logging without a reload (the wrapped fetch still
// calls through — it just stops appending). See networkTrace.ts for the removal checklist.

type ConnectionLike = {
	type?: string;
	effectiveType?: string;
	downlink?: number;
	rtt?: number;
	addEventListener?: (type: "change", listener: () => void) => void;
};

const getConnection = (): ConnectionLike | undefined =>
	(navigator as Navigator & { connection?: ConnectionLike }).connection;

// host + path only — drop the query string so tokens/ids in URLs are never written to the buffer.
const shortenUrl = (url: string): string => {
	try {
		const parsed = new URL(url, window.location.origin);
		return `${parsed.host}${parsed.pathname}`;
	} catch {
		return url;
	}
};

const readRequest = (input: RequestInfo | URL, init?: RequestInit): { method: string; url: string } => {
	if (typeof input === "string") {
		return { method: init?.method ?? "GET", url: input };
	}
	if (input instanceof URL) {
		return { method: init?.method ?? "GET", url: input.toString() };
	}
	return { method: input.method ?? init?.method ?? "GET", url: input.url };
};

const installFetchWrapper = () => {
	const originalFetch = window.fetch.bind(window);
	window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
		if (!shouldTraceNetworkDebug()) {
			return originalFetch(input, init);
		}
		const { method, url } = readRequest(input, init);
		// AppSync GraphQL POSTs are already logged by name via the Apollo debug link — skip the
		// duplicate raw line here.
		if (url.startsWith(getTerminalConfig().aws.appsyncUrl)) {
			return originalFetch(input, init);
		}
		const start = Date.now();
		try {
			const response = await originalFetch(input, init);
			networkTrace(`[http] ${method} ${shortenUrl(url)} → ${response.status} ${Date.now() - start}ms`);
			return response;
		} catch (error) {
			networkTrace(`[http] ${method} ${shortenUrl(url)} → ERROR ${Date.now() - start}ms`);
			throw error;
		}
	};
};

const installConnectivityListeners = () => {
	window.addEventListener("online", () => {
		if (shouldTraceNetworkDebug()) {
			networkTrace("[conn] online (navigator.onLine=true)");
		}
	});
	window.addEventListener("offline", () => {
		if (shouldTraceNetworkDebug()) {
			networkTrace("[conn] offline (navigator.onLine=false)");
		}
	});

	// Network Information API — unsupported on iOS WKWebView, so feature-detect rather than assume.
	const connection = getConnection();
	connection?.addEventListener?.("change", () => {
		if (!shouldTraceNetworkDebug()) {
			return;
		}
		const current = getConnection();
		networkTrace(
			`[conn] netinfo type=${current?.type ?? "-"} effectiveType=${current?.effectiveType ?? "-"} downlink=${current?.downlink ?? "-"} rtt=${current?.rtt ?? "-"}`,
		);
	});
};

/**
 * Installs the network diagnostic instrumentation once. No-op when the flag is off, so importing and
 * calling this unconditionally at startup costs nothing for normal customer tablets.
 */
export const installNetworkDebug = (): void => {
	if (window.__networkDebugInstalled || !shouldTraceNetworkDebug()) {
		return;
	}
	window.__networkDebugInstalled = true;

	installFetchWrapper();
	installConnectivityListeners();

	const connection = getConnection();
	networkTrace(
		`[conn] init navigator.onLine=${navigator.onLine} netinfo=${connection ? `${connection.effectiveType ?? "-"}/${connection.downlink ?? "-"}Mbps` : "unsupported"}`,
	);
};
